'use strict';

const localStorageSlim = require('localstorage-slim');
const {
	CSRF_TOKEN_CACHE_TTL_SECONDS,
	fetchTokenViaJquery,
	getCsrfTokenCacheKey,
	pruneExpiredCsrfTokenCacheEntries,
	setCsrfSessionScope,
} = require('public/js/lib/csrf/token-fetcher');
const { setMetaToken, setHiddenInputToken, injectTokenIntoForms, startFormInjectionObserver } = require('public/js/lib/csrf/form-injector');
const { installFormSubmitInterceptor, installJQuerySubmitHooks } = require('public/js/lib/csrf/submit-guard');
const { CSRF_FAILURE_ERROR_CODE, CSRF_FAILURE_ERROR_HEADER, installAjaxDeferralWrapper, installCsrfAjaxHooks, notifyCsrfTimeout } = require('public/js/lib/csrf/ajax-hooks');

const CSRF_READY_TIMEOUT_MS = 20000;

/**
 * @param {{ token: string, fromCache: boolean, sessionScope: string }} result
 * @returns {string}
 */
function applyTokenResult({ token, fromCache, sessionScope }) {
	if (!token) {
		return '';
	}

	setCsrfSessionScope(sessionScope);
	if (!fromCache) {
		localStorageSlim.set(getCsrfTokenCacheKey(sessionScope), token, { ttl: CSRF_TOKEN_CACHE_TTL_SECONDS });
	}
	setMetaToken(token);
	setHiddenInputToken(token);
	injectTokenIntoForms();
	startFormInjectionObserver();
	return token;
}

/**
 * @returns {Promise<string>}
 */
function refreshCsrfToken() {
	return Promise.resolve()
		.then(() => fetchTokenViaJquery())
		.then(applyTokenResult)
		.catch((e) => {
			console.error('[csrf-bootstrap] on-demand token refresh failed', e); // eslint-disable-line no-console
			return '';
		});
}

/**
 * Races a promise against a timeout so consumers are never blocked indefinitely.
 * @param {Promise} promise
 * @returns {Promise}
 */
function raceWithTimeout(promise) {
	return new Promise(function (resolve, reject) {
		const timerId = setTimeout(function () { reject(new Error('csrf_ready_timeout')); }, CSRF_READY_TIMEOUT_MS);
		promise.then(
			function (value) { clearTimeout(timerId); resolve(value); },
			function (reason) { clearTimeout(timerId); reject(reason); }
		);
	});
}

/**
 * Fetches a CSRF token and installs it into:
 * - meta[name="csrf-token"]
 * - hidden input fallback (#__csrf_value_field__)
 *
 * Also installs jQuery ajaxSend hooks to attach CSRF headers to
 * same-origin mutating requests (for both RP and global jQuery instances).
 *
 * @returns {Promise<string>} resolves to the fetched token (or '' on failure)
 */
function initCsrfBootstrap() {
	if (globalThis.__rp_csrf_ready_promise) {
		return globalThis.__rp_csrf_ready_promise;
	}

	pruneExpiredCsrfTokenCacheEntries();

	const ready = Promise.resolve()
		.then(() => fetchTokenViaJquery())
		.then(applyTokenResult)
		.catch((e) => {
			console.error('[csrf-bootstrap] token fetch failed — CSRF protection inactive', e); // eslint-disable-line no-console
			return '';
		});

	globalThis.__rp_csrf_ready_promise = ready;

	// jQuery/rp$ may load asynchronously after bootstrap starts. Install eagerly
	// for immediate availability, then re-attempt after the token fetch settles
	// (success or failure) to catch late-loaded instances. Each function is
	// idempotent via __rp_csrf_ajax_hook_installed / __rp_csrf_wrapped guards.
	try {
		installAjaxDeferralWrapper(raceWithTimeout);
		installCsrfAjaxHooks();
		installFormSubmitInterceptor(raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout);
		installJQuerySubmitHooks(raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout);
		ready.then(() => {
			installCsrfAjaxHooks();
			installJQuerySubmitHooks(raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout);
		}).catch(() => {
			installCsrfAjaxHooks();
			installJQuerySubmitHooks(raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout);
		});
	} catch (e) {
		console.error('[csrf-bootstrap] hook installation failed — CSRF protection may be incomplete', e); // eslint-disable-line no-console
	}

	return ready;
}

module.exports = {
	CSRF_FAILURE_ERROR_CODE,
	CSRF_FAILURE_ERROR_HEADER,
	CSRF_READY_TIMEOUT_MS,
	initCsrfBootstrap,
	refreshCsrfToken,
};
